import { useEffect, useRef } from 'react';

import { useCommentsTracking } from './useCommentsTracking';

/**
 * Minimum visibility ratio (0-1) required to start the timer
 */
const VISIBILITY_THRESHOLD = 0.8;

/**
 * Duration in milliseconds the comment must be visible before marking as read
 */
const VISIBILITY_DURATION = 3000;

export interface UseMarkAsReadOnVisibilityOptions {
  commentId: string;

  isUnread: boolean;
}

/**
 * Custom hook to automatically mark a comment as read when it's visible in the viewport
 * for a specified duration.
 *
 * @param options - Configuration options
 * @returns A ref to attach to the comment element
 *
 * @example
 * ```tsx
 * const commentRef = useMarkAsReadOnVisibility({
 *   commentId: id,
 *   isUnread: isUnread,
 * });
 *
 * return <div ref={commentRef}>Comment content...</div>
 * ```
 */
export const useMarkAsReadOnVisibility = <T extends HTMLElement = HTMLDivElement>({
  commentId,
  isUnread,
}: UseMarkAsReadOnVisibilityOptions) => {
  const elementRef = useRef<T>(null);
  const visibilityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTimerActiveRef = useRef(false);
  const { markCommentsAsRead } = useCommentsTracking();

  useEffect(() => {
    // Clean up timer if comment is no longer unread
    if (!isUnread) {
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
        visibilityTimerRef.current = null;
        isTimerActiveRef.current = false;
      }
      return;
    }

    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio >= VISIBILITY_THRESHOLD) {
            // Only start timer if one isn't already active
            if (!isTimerActiveRef.current) {
              isTimerActiveRef.current = true;
              // Capture commentId in closure to ensure we mark the correct comment
              const capturedCommentId = commentId;
              visibilityTimerRef.current = setTimeout(() => {
                markCommentsAsRead([capturedCommentId]);
                isTimerActiveRef.current = false;
                visibilityTimerRef.current = null;
              }, VISIBILITY_DURATION);
            }
          } else {
            // If visibility drops below threshold, cancel the timer
            if (visibilityTimerRef.current) {
              clearTimeout(visibilityTimerRef.current);
              visibilityTimerRef.current = null;
              isTimerActiveRef.current = false;
            }
          }
        });
      },
      {
        threshold: [0, VISIBILITY_THRESHOLD],
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
      // Clean up timer on unmount
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
        visibilityTimerRef.current = null;
        isTimerActiveRef.current = false;
      }
    };
  }, [isUnread, commentId, markCommentsAsRead]);

  return elementRef;
};
